import { generatePath } from 'react-router';

import { Button, Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FieldLabel } from 'Components/FieldLabel/FieldLabel';
import { ThirdPartyLocation } from 'Components/TPRM/ThirdPartyLocation/ThirdPartyLocation';
import { TagListing } from 'Components/Tags/TagListing/TagListing';
import { Text } from 'Components/Text/Text';
import { UserDefinedReference } from 'Components/UserDefinedReference/UserDefinedReference';
import { TPRM_FOLDERS_LISTING, TPRM_ISSUES_EXCEPTIONS } from 'Config/PathTemplates';
import { TPRM_THIRD_PARTY_EIN, TPRM_THIRD_PARTY_MANAGER, TPRM_THIRD_PARTY_SERVICE_DELEGATES, TPRM_THIRD_PARTY_SERVICE_DESCRIPTION, TPRM_THIRD_PARTY_SERVICE_MANAGER, TPRM_THIRD_PARTY_SERVICE_RESPONSIBLE_ORGANIZATION } from 'Config/Tooltips';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { ellipt } from 'Helpers/StringUtils';
import { SEARCH_PARAM_FOLDER_PRESELECTED_ORGANIZATION_ID, SEARCH_PARAM_ISSUES_EXCEPTIONS_ORGANIZATION_ID } from 'Helpers/URLBuilder/URLBuilder';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { countOpenExceptions } from 'Models/Exceptions';
import { FolderVersion } from 'Models/Folders';
import { countOpenIssues } from 'Models/Issues';
import { ServiceForNonTprmUser, ThirdPartyContact, ThirdPartyResponseWithServices, ThirdPartyService, ThirdPartyWithServicesForNonTprmUser } from 'Models/TPRM';
import { UserResponse } from 'Models/User';

import styles from './ThirdPartiesAndServicesTable.module.css';

export interface ThirdPartiesAndServicesTableExpandedInformationProps {
    folders: FolderVersion[];
    onDisplayContacts: (contacts: ThirdPartyContact[], header: string) => void;
    onDisplayUsers: (userIds: string[], header: string) => void;
    thirdParty: ThirdPartyResponseWithServices | ThirdPartyWithServicesForNonTprmUser;
    userHasFullTprmAccess: boolean;

    service?: ThirdPartyService | ServiceForNonTprmUser;
}

export const ThirdPartiesAndServicesTableExpandedInformation = (props: ThirdPartiesAndServicesTableExpandedInformationProps) => {
    const cachedData = useCachedData();

    const { thirdParty, service, userHasFullTprmAccess } = props;

    return (
        <div className={styles.collapsedInformationContainer}>
            <div className={styles.collapsedThirdPartyInformation}>
                <Text variant="Header3">{props.thirdParty.name}</Text>
                <div className={styles.detailsContainer}>
                    <div className={styles.detailsGridSection}>
                        <div className={styles.detailsGridColumn}>
                            <div className={styles.labelAndValueContainerInsideOfGrid}>
                                <FieldLabel fieldName="Manager" tooltipText={TPRM_THIRD_PARTY_MANAGER} />
                                <Text>{getUserNameFromSubject(thirdParty.vendor_manager_user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST) ?? ''}</Text>
                            </div>
                            <div className={styles.labelAndValueContainerInsideOfGrid}>
                                <FieldLabel fieldName="Employer ID Number" tooltipText={TPRM_THIRD_PARTY_EIN} />
                                <Text>{thirdParty.ein ?? '-'}</Text>
                            </div>
                            {userHasFullTprmAccess && (
                                <div className={styles.labelAndValueContainerInsideOfGrid}>
                                    <FieldLabel fieldName="Issues" />
                                    <Link size="sm" to={{ pathname: TPRM_ISSUES_EXCEPTIONS, search: `${SEARCH_PARAM_ISSUES_EXCEPTIONS_ORGANIZATION_ID}=${thirdParty.id}`, hash: '#issues' }}>
                                        {getOpenIssuesLabel(thirdParty as ThirdPartyResponseWithServices)}
                                    </Link>
                                </div>
                            )}
                        </div>
                        <div className={styles.detailsGridColumn}>
                            <div className={styles.labelAndValueContainerInsideOfGrid}>
                                <FieldLabel fieldName="Created" />
                                <Text>{getCreatedString(cachedData.users, thirdParty.created_time, thirdParty.created_by)}</Text>
                            </div>
                            <div className={styles.labelAndValueContainerInsideOfGrid}>
                                <FieldLabel fieldName="Contacts" />
                                <Button variant="linkText" size="sm" onClick={() => props.onDisplayContacts(thirdParty.vendor_contacts, 'Third-Party Contacts')}>{`${thirdParty.vendor_contacts.length} ${thirdParty.vendor_contacts.length === 1 ? 'contact' : 'contacts'}`}</Button>
                            </div>
                            {userHasFullTprmAccess && (
                                <div className={styles.labelAndValueContainerInsideOfGrid}>
                                    <FieldLabel fieldName="Exceptions" />
                                    <Link size="sm" to={{ pathname: TPRM_ISSUES_EXCEPTIONS, search: `${SEARCH_PARAM_ISSUES_EXCEPTIONS_ORGANIZATION_ID}=${thirdParty.id}`, hash: '#exceptions' }}>
                                        {getOpenExceptionsLabel(thirdParty as ThirdPartyResponseWithServices)}
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                    {userHasFullTprmAccess && (
                        <div className={styles.labelAndValueContainerInsideOfGrid}>
                            <FieldLabel fieldName="Folders" />
                            <Link size="sm" to={{ pathname: generatePath(TPRM_FOLDERS_LISTING), search: `${SEARCH_PARAM_FOLDER_PRESELECTED_ORGANIZATION_ID}=${thirdParty.id}` }}>
                                {props.folders.length === 1 ? '1 folder' : `${props.folders.length} folders`}
                            </Link>
                        </div>
                    )}
                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                        <FieldLabel fieldName="Website" />
                        <UserDefinedReference reference={thirdParty.website} />
                    </div>
                    <div className={styles.labelAndValueContainerOutsideOfGrid}>
                        <FieldLabel fieldName="Location" />
                        <ThirdPartyLocation thirdPartyMetadata={thirdParty} variant="nonDashboard" />
                    </div>
                    <div className={styles.labelAndValueContainerOutsideOfGrid}>
                        <FieldLabel fieldName="Tags" />
                        {thirdParty.tags.length > 0 ? <TagListing tags={cachedData.convertTagIdsToDisplayableTags(thirdParty.tags)} showAll /> : <Text>-</Text>}
                    </div>
                </div>
            </div>
            {service && (
                <>
                    <div>
                        <div className={styles.collapsedInformationSeparator} />
                    </div>
                    <div className={styles.collapsedServiceInformation}>
                        <Text variant="Header3">{service.name}</Text>
                        <div className={styles.detailsContainer}>
                            <div className={styles.detailsGridSection}>
                                <div className={styles.detailsGridColumn}>
                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                        <FieldLabel fieldName="Manager" tooltipText={TPRM_THIRD_PARTY_SERVICE_MANAGER} />
                                        <Text>{getUserNameFromSubject(service.vendor_service_manager_user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST) ?? ''}</Text>
                                    </div>
                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                        <FieldLabel fieldName="Last Assessed" />
                                        <Text>{service.last_assessed ? iso8601ToUsDateShort(service.last_assessed) : '-'}</Text>
                                    </div>
                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                        <FieldLabel fieldName="Delegates" tooltipText={TPRM_THIRD_PARTY_SERVICE_DELEGATES} />
                                        <Button variant="linkText" size="sm" onClick={() => props.onDisplayUsers(service.delegates, 'Delegates')}>{`${service.delegates.length} ${service.delegates.length === 1 ? 'delegate' : 'delegates'}`}</Button>
                                    </div>
                                </div>
                                <div className={styles.detailsGridColumn}>
                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                        <FieldLabel fieldName="Created" />
                                        <Text>{getCreatedString(cachedData.users, service.created_time, service.created_by)}</Text>
                                    </div>
                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                        <FieldLabel fieldName="Responsible Organization" tooltipText={TPRM_THIRD_PARTY_SERVICE_RESPONSIBLE_ORGANIZATION} />
                                        <Text>{service.responsible_organization ? ellipt(service.responsible_organization) : '-'}</Text>
                                    </div>
                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                        <FieldLabel fieldName="Contacts" />
                                        <Button variant="linkText" size="sm" onClick={() => props.onDisplayContacts(service.vendor_contacts, 'Third-Party Service Contacts')}>{`${service.vendor_contacts.length} ${service.vendor_contacts.length === 1 ? 'contact' : 'contacts'}`}</Button>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.labelAndValueContainerOutsideOfGrid}>
                                <FieldLabel fieldName="Inherent Risk Questionnaire Due Date" />
                                <Text>{service.assessment_workflow_setup.irq_target_completion_date ? iso8601ToUsDateShort(service.assessment_workflow_setup.irq_target_completion_date) : '-'}</Text>
                            </div>
                            <div className={styles.labelAndValueContainerOutsideOfGrid}>
                                <FieldLabel fieldName="Description" tooltipText={TPRM_THIRD_PARTY_SERVICE_DESCRIPTION} />
                                <Text>{service.description}</Text>
                            </div>
                            <div className={styles.labelAndValueContainerOutsideOfGrid}>
                                <FieldLabel fieldName="Tags" />
                                {service.tags.length > 0 ? <TagListing tags={cachedData.convertTagIdsToDisplayableTags(service.tags)} showAll /> : <Text>-</Text>}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const getCreatedString = (users: UserResponse[], created_time: string, createdByUserId?: string) => (created_time && createdByUserId ? `${iso8601ToUsDateShort(created_time)} by ${getUserNameFromSubject(createdByUserId, users, UserNameFormat.FIRST_SPACE_LAST)}` : iso8601ToUsDateShort(created_time));

// TODO: These will potentially be repeated multiple times per vendor. Consider memoizing a Map.
const getOpenIssuesLabel = (thirdParty: ThirdPartyResponseWithServices) => {
    const openIssues = countOpenIssues(thirdParty.issues);
    return openIssues === 1 ? '1 open issue' : `${openIssues} open issues`;
};
const getOpenExceptionsLabel = (thirdParty: ThirdPartyResponseWithServices) => {
    const openExceptions = countOpenExceptions(thirdParty.exceptions);
    return openExceptions === 1 ? '1 open exception' : `${openExceptions} open exceptions`;
};
